import React from 'react';
import { Icon } from 'antd';
import styled from 'styled-components';
import { COMMUNICATION_GENERATOR } from './translationValues';
import { useTranslation } from 'react-i18next';

const NAMESPACE = ['COMMUNICATION_GENERATOR'];

const StyledButton = styled.button`
  height: 44px;
  border-radius: 8px;
  font-weight: 600;
  padding: 0 24px;
  cursor: pointer;
  transition: all 0.3s;
  font-family: inherit;

  &.action-button-default {
    background: white;
    border: 2px solid #e0e0e0;
    color: #333;

    &:hover {
      background: #f5f5f5;
      border-color: #e0e0e0;
      color: #333;
    }
  }

  &.action-button-primary {
    background: #667eea;
    border: none;
    color: white;

    &:hover {
      background: #5568d3;
      color: white;
    }
  }

  &.action-button-reset {
    background: #fff;
    border: 2px solid #f5222d;
    color: #f5222d;

    &:hover {
      background: #fff1f0;
      border-color: #f5222d;
      color: #f5222d;
    }
  }
`;

const StyledTag = styled.span`
  padding: 6px 12px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 13px;
  border: none;
  display: inline-block;

  &.tag-gold {
    background: #ffd700;
    color: #333;
  }

  &.tag-purple {
    background: #9b59b6;
    color: white;
  }

  &.tag-cyan {
    background: #00bcd4;
    color: white;
  }
`;

const StyledAlert = styled.div`
  margin-bottom: 24px;
  padding: 16px;
  background: #f6ffed;
  border: 1px solid #b7eb8f;
  border-radius: 8px;

  .alert-message {
    display: flex;
    align-items: center;
    gap: 12px;
    color: #389e0d;
    font-weight: 600;
  }
`;

const PreviewCard = styled.div`
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  animation: slideInFromRight 0.4s cubic-bezier(0.4, 0, 0.2, 1);

  @keyframes slideInFromRight {
    from {
      opacity: 0;
      transform: translateX(30px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
`;

const PreviewHeader = styled.div`
  padding: 32px 40px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
`;

const PreviewTitle = styled.h2`
  color: white;
  margin: 0 0 12px 0;
  font-size: 28px;
`;

const PreviewMeta = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
`;

const DocumentContent = styled.div`
  padding: 40px;
  background: #fafafa;
`;

const ActionBar = styled.div`
  padding: 24px 40px;
  background: white;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  border-top: 1px solid #f0f0f0;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    padding: 16px 20px;
  }
`;

export function DocumentPreview({ communicationDetails, channel, language, onReset, onBack }) {
  const { t } = useTranslation(NAMESPACE);

  return (
    <PreviewCard>
      <PreviewHeader>
        <PreviewTitle>
          <span>✅</span>{' '}
          {channel === 'print'
            ? t(
                COMMUNICATION_GENERATOR.DOCUMENT_GENERATED_SUCCESS_MESSAGE.key,
                COMMUNICATION_GENERATOR.DOCUMENT_GENERATED_SUCCESS_MESSAGE.value,
              )
            : t(
                COMMUNICATION_GENERATOR.EMAIL_SENT_SUCCESS_MESSAGE.key,
                COMMUNICATION_GENERATOR.EMAIL_SENT_SUCCESS_MESSAGE.value,
              )}
        </PreviewTitle>
        <PreviewMeta>
          <StyledTag className="tag-gold">
            {channel === 'email' ? (
              <>
                <Icon type="mail" />{' '}
                {t(
                  COMMUNICATION_GENERATOR.EMAIL_LABEL.key,
                  COMMUNICATION_GENERATOR.EMAIL_LABEL.value,
                )}
              </>
            ) : (
              <>
                <Icon type="printer" />{' '}
                {t(
                  COMMUNICATION_GENERATOR.DOCUMENT_LABEL.key,
                  COMMUNICATION_GENERATOR.DOCUMENT_LABEL.value,
                )}
              </>
            )}
          </StyledTag>
          <StyledTag className="tag-purple">
            <Icon type="global" /> {language}
          </StyledTag>
          <StyledTag className="tag-cyan">
            <Icon type="file-text" /> {communicationDetails.communicationName}
          </StyledTag>
        </PreviewMeta>
      </PreviewHeader>

      <DocumentContent>
        <StyledAlert>
          <div className="alert-message">
            <span style={{ fontSize: '20px' }}>✅</span>
            {channel === 'print' ? (
              <span>
                {t(
                  COMMUNICATION_GENERATOR.DOCUMENT_GENERATED_DESCRIPTION.key,
                  COMMUNICATION_GENERATOR.DOCUMENT_GENERATED_DESCRIPTION.value,
                )}
              </span>
            ) : (
              <span>
                {t(
                  COMMUNICATION_GENERATOR.EMAIL_SENT_DESCRIPTION.key,
                  COMMUNICATION_GENERATOR.EMAIL_SENT_DESCRIPTION.value,
                )}
              </span>
            )}
          </div>
        </StyledAlert>
      </DocumentContent>

      <ActionBar>
        <StyledButton className="action-button-default" onClick={onBack}>
          <Icon type="edit" />{' '}
          {t(
            COMMUNICATION_GENERATOR.EDIT_FORM_BUTTON.key,
            COMMUNICATION_GENERATOR.EDIT_FORM_BUTTON.value,
          )}
        </StyledButton>
        <StyledButton className="action-button-reset" onClick={onReset}>
          <Icon type="retweet" />{' '}
          {t(
            COMMUNICATION_GENERATOR.START_NEW_BUTTON.key,
            COMMUNICATION_GENERATOR.START_NEW_BUTTON.value,
          )}
        </StyledButton>
      </ActionBar>
    </PreviewCard>
  );
}
